import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { createHash } from "node:crypto";

import { submitLeadSchema, type SubmitLeadInput } from "./schemas";
import { isQuickQuoteTest } from "./test-mode.server";
import { isPreviewHost } from "./lead-host.server";

/**
 * Single submission entry point for every public form on the site.
 * 1. Validates input (per-form Zod schema)
 * 2. Verifies the Cloudflare Turnstile token
 * 3. Inserts a durable row in D1 `form_leads` (pending). If D1 is down or
 *    slow (3 s), it inserts into the Supabase table `form_submissions`, as
 *    before 7 Oct 2026.
 * 4. POSTs to Salesforce Web-to-Lead (under a D1 claim for a D1 row)
 * 5. Updates the row with the outcome
 *
 * Fault tests (valid x-rh-test AND x-rh-fault) skip Turnstile, use the D1
 * table form_test_leads and a stand-in Salesforce post, never write to
 * Supabase and never email HR. A visitor cannot reach this mode: x-rh-fault
 * is read only after the test secret matched.
 *
 * Always returns `{ ok: true }` on validated input — the lead is stored
 * even if Salesforce is down, and an internal replay job can resend.
 * Throws only on validation or bot-check failure.
 */
export const submitLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => submitLeadSchema.parse(data))
  .handler(async ({ data }) => {
    const input: SubmitLeadInput = data;

    const userAgent = getRequestHeader("user-agent") ?? null;
    const ip = getRequestIP({ xForwardedFor: true }) ?? null;
    const ipHash = ip
      ? createHash("sha256").update(ip).digest("hex").slice(0, 32)
      : null;

    // A preview host may name faults without the test secret (14 Oct 2026):
    // it only ever reaches the preview D1/R2 test tables and stand-ins.
    const faultHeader = isQuickQuoteTest() || isPreviewHost() ? getRequestHeader("x-rh-fault") : undefined;
    // A non-production host (a branch preview) is always a fault test with
    // no faults (8 Oct 2026): D1 form_test_leads and the stand-in post only.
    const faultTest = (faultHeader !== undefined && faultHeader !== null) || isPreviewHost();
    const faults = new Set((faultHeader ?? "").split(",").map((f) => f.trim().toLowerCase()));

    if (!faultTest) {
      const { verifyTurnstile } = await import("./turnstile.server");
      const turnstile = await verifyTurnstile(
        input.turnstile_token,
        ip ?? undefined,
        input.form_key,
      );
      if (!turnstile.ok) {
        console.warn("[submitLead] turnstile rejected", {
          form_key: input.form_key,
          reason: turnstile.reason,
        });
        throw new Error("Verification failed. Please try again.");
      }
    }

    // Strip the bot token (and any large file upload) from the persisted payload.
    const { turnstile_token: _t, ...rest } = input as SubmitLeadInput & {
      resume?: { filename: string; mime: string; data: string };
    };
    void _t;
    const resume = "resume" in rest ? rest.resume : undefined;
    if ("resume" in rest) delete (rest as { resume?: unknown }).resume;

    // Write-ahead (7 Oct 2026): the submission goes to D1 first. If D1 is
    // unavailable, the Supabase row is written instead, exactly as before.
    // Persisting the submission must never block the lead itself: if no
    // store is reachable we still hand the lead to Salesforce.
    const { openFormQueue, formSender } = await import("./form-leads-queue.server");
    const formQueue = openFormQueue({ test: faultTest, d1Down: faults.has("d1") });
    const d1Id = crypto.randomUUID();
    const inD1 = await formQueue.insert(d1Id, input.form_key, rest);
    const { captureFail } = await import("./lead-rescue.server");
    const failures: string[] = [];
    if (!inD1) {
      failures.push(
        captureFail("d1", "form", d1Id, formQueue.available ? "form insert failed or timed out" : "no D1 binding", faultTest),
      );
    }

    let submissionId: string | null = null;
    if (!inD1 && !faultTest) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const insert = await supabaseAdmin
          .from("form_submissions")
          .insert({
            form_key: input.form_key,
            page_path: input.page_path ?? null,
            email: input.email ?? null,
            phone: "phone" in input ? input.phone ?? null : null,
            first_name: input.first_name ?? null,
            last_name: input.last_name ?? null,
            city: "city" in input ? input.city ?? null : null,
            state: "state" in input ? input.state ?? null : null,
            preferred_clinic:
              "preferred_clinic" in input ? input.preferred_clinic ?? null : null,
            payload: rest,
            salesforce_status: "pending",
            salesforce_attempts: 0,
            user_agent: userAgent,
            ip_hash: ipHash,
            referrer: input.referrer ?? null,
            utm: input.utm ?? null,
          })
          .select("id")
          .single();
        if (insert.error || !insert.data) {
          console.error("[submitLead] failed to persist submission", insert.error);
          failures.push(captureFail("supabase", "form", d1Id, insert.error?.message ?? "no row returned", faultTest));
        } else {
          submissionId = insert.data.id;
        }
      } catch (e) {
        console.error("[submitLead] submission storage unavailable", e);
        failures.push(captureFail("supabase", "form", d1Id, e, faultTest));
      }
    }

    // Fire-and-update Salesforce. We await it so the stored row reflects the
    // current outcome, but we never fail the user response over it. A D1 row
    // is posted under its claim, so the replay job cannot post it as well.
    const send = formSender(faultTest, faults.has("salesforce"));
    const payloadForSf = {
      ...input,
      ...(resume ? { resume: undefined } : {}),
    } as unknown as Record<string, unknown>;
    let sf: Awaited<ReturnType<typeof send>>;
    if (inD1) {
      const token = await formQueue.claim(d1Id);
      if (token) {
        sf = await send(d1Id, payloadForSf);
        await formQueue.release(d1Id, token, sf.ok ? { ok: true } : { ok: false, error: sf.error });
      } else {
        // Claimed elsewhere already: the lead is stored and will be sent.
        await formQueue.bumpDuplicatesBlocked(d1Id);
        sf = { ok: false, status: 0, error: "claimed_elsewhere" };
      }
    } else {
      sf = await send(d1Id, payloadForSf);
    }

    if (!sf.ok && sf.error !== "claimed_elsewhere") {
      failures.push(captureFail("salesforce", "form", d1Id, sf.error, faultTest));
    }

    // Zero loss (14 Oct 2026): no store took the lead and Salesforce did not
    // either. Keep it in the R2 rescue store; the drain moves it into
    // form_leads and the form replay posts it. Only if R2 fails as well does
    // the visitor see the error.
    if (!sf.ok && !submissionId && !inD1) {
      const { openRescue, alertCapture, captureLost } = await import("./lead-rescue.server");
      const { rawQueueDb } = await import("./quick-quote-queue.server");
      const rescue = openRescue({ test: faultTest, faults });
      const record = {
        kind: "form" as const,
        id: d1Id,
        form_key: input.form_key,
        payload: rest as Record<string, unknown>,
        failures,
        created_at: new Date().toISOString(),
      };
      if (await rescue.put(record)) {
        await alertCapture({ event: "rescued", kind: "form", leadId: d1Id, failures }, faultTest, rawQueueDb());
        return { ok: true, id: d1Id };
      }
      const lost = [...failures, "r2: rescue write failed"];
      captureLost("form", d1Id, lost, faultTest);
      await alertCapture(
        {
          event: "lost",
          kind: "form",
          leadId: d1Id,
          failures: lost,
          contact: {
            name: `${input.first_name ?? ""} ${input.last_name ?? ""}`.trim(),
            email: input.email ?? undefined,
            phone: "phone" in input ? input.phone ?? undefined : undefined,
            form: input.form_key,
          },
        },
        faultTest,
        rawQueueDb(),
      );
      throw new Error("Could not save your submission. Please try again, or call (866) 488-9356.");
    }

    if (submissionId) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin
          .from("form_submissions")
          .update({
            salesforce_status: sf.ok ? "sent" : "failed",
            salesforce_attempts: 1,
            salesforce_last_attempt_at: new Date().toISOString(),
            salesforce_response: sf.ok ? `http_${sf.status}` : null,
            error: sf.ok ? null : sf.error,
          })
          .eq("id", submissionId);
      } catch (e) {
        console.error("[submitLead] status update failed", e);
      }
    }

    // Careers applications are also emailed to HR + client relations.
    if (
      !faultTest &&
      input.form_key === "contact" &&
      (input.page_path ?? "").startsWith("/careers")
    ) {
      try {
        const { emailCareersApplication } = await import("./careers-mail.server");
        await emailCareersApplication({
          applicantName: `${input.first_name} ${input.last_name}`.trim(),
          fields: [
            {
              label: "Name",
              value: `${input.first_name} ${input.last_name}`.trim(),
            },
            { label: "Email", value: input.email ?? "" },
            { label: "Phone", value: input.phone ?? "" },
            { label: "Desired Job Title", value: input.job_title ?? "" },
            {
              label: "Resume",
              value: resume ? `Attached (${resume.filename})` : "(not provided)",
            },
            {
              label: "Comments",
              value: (input.message ?? "").replace(
                /^Careers inquiry\nDesired Job Title:.*\n\n/u,
                "",
              ),
            },
            { label: "Submitted From", value: input.page_path ?? "" },
            {
              label: "Submitted At",
              value: `${new Date().toLocaleString("en-US", {
                timeZone: "America/Chicago",
              })} CT`,
            },
          ],
          attachment: resume,
        });
      } catch (err) {
        console.error("[submitLead] careers email failed", err);
      }
    }

    return { ok: true, id: submissionId ?? (inD1 ? d1Id : null) };
  });